import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, Star, Award, Users, Check, X, Target, Trophy } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LEVELS = ['beginner', 'intermediate', 'advanced', 'professional', 'olympic'];

function FindCoach() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: input, 2: results
  const [formData, setFormData] = useState({
    sport: '',
    level: 'beginner'
  });
  const [matches, setMatches] = useState([]);
  const [proposalId, setProposalId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sportsCategories, setSportsCategories] = useState([]);
  
  useEffect(() => {
    fetchSportsCategories();
  }, []);
  
  const fetchSportsCategories = async () => {
    try {
      const response = await axios.get(`${API}/sports/categories`);
      setSportsCategories(response.data);
    } catch (error) {
      console.error('Error fetching sports categories:', error);
    }
  };
  
  const handleFindCoach = async (e) => {
    e.preventDefault();
    
    // Validate sport is selected
    if (!formData.sport) {
      toast.error('Please select a sport');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/matching/find-coach`, {
        sport: formData.sport,  // Changed from sport_category to sport
        level: formData.level
      }, { withCredentials: true });
      
      setMatches(response.data.matches || []);
      setProposalId(response.data.proposal_id);
      setStep(2);
    } catch (error) {
      console.error('Error finding coaches:', error);
      toast.error(error.response?.data?.detail || 'Error finding coaches');
    } finally {
      setLoading(false);
    }
  };
  
  const handleAcceptMatch = async (coachId) => {
    try {
      await axios.post(`${API}/matching/accept`, {
        proposal_id: proposalId,
        coach_id: coachId
      }, { withCredentials: true });
      
      toast.success('Connection request sent!');
      navigate('/messages');
    } catch (error) {
      console.error('Error accepting match:', error);
      toast.error(error.response?.data?.detail || 'Error sending request');
    }
  };
  
  const handleDefer = async () => {
    try {
      if (proposalId) {
        await axios.post(`${API}/matching/defer`, {
          proposal_id: proposalId
        }, { withCredentials: true });
      }
      navigate('/courses');
    } catch (error) {
      console.error('Error deferring:', error);
      navigate('/courses');
    }
  };
  
  return (
    <div className="min-h-screen bg-deep-dark py-12" data-testid="find-coach-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {step === 1 ? (
          <>
            <div className="text-center mb-8 sm:mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-premium-gold rounded-2xl mb-4 sm:mb-6 shadow-glow-red">
                <Target className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
              </div>
              <h1 
                className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white mb-3 sm:mb-4 px-4"
                style={{ fontFamily: 'Fraunces, serif' }}
                data-testid="page-title"
              >
                Find Your <span className="gold-gradient">Perfect Coach</span>
              </h1>
              <p className="text-base sm:text-lg text-white/70 max-w-2xl mx-auto px-4">
                AI-powered matching to connect you with the best coach for your goals
              </p>
            </div>
            
            <div className="bg-dark-surface p-6 sm:p-8 rounded-xl border border-premium-gold/30/50 max-w-2xl mx-auto">
              <form onSubmit={handleFindCoach} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-white/80 mb-2">
                    Which sport do you want to learn? <span className="text-premium-gold">*</span>
                  </label>
                  <Select 
                    value={formData.sport} 
                    onValueChange={(value) => setFormData({...formData, sport: value})}
                    required
                  >
                    <SelectTrigger className="w-full h-12 sm:h-auto bg-deep-dark border-premium-gold/30 text-white text-base" data-testid="sport-select">
                      <SelectValue placeholder="Select a sport" />
                    </SelectTrigger>
                    <SelectContent className="max-h-80 bg-dark-surface border-premium-gold/30">
                      {sportsCategories.map((category) => (
                        <React.Fragment key={category.category_id}>
                          <div className="px-2 py-1.5 text-xs font-bold text-premium-gold bg-deep-dark/50 tracking-wider">
                            {category.icon} {category.category_name}
                          </div>
                          {category.sports.map((sport) => (
                            <SelectItem key={sport.id} value={sport.id} className="text-white hover:bg-deep-dark py-3">
                              {sport.icon} {sport.name}
                            </SelectItem>
                          ))}
                        </React.Fragment>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-white/80 mb-2">
                    Your current level <span className="text-premium-gold">*</span>
                  </label>
                  <Select 
                    value={formData.level} 
                    onValueChange={(value) => setFormData({...formData, level: value})}
                  >
                    <SelectTrigger className="w-full h-12 sm:h-auto bg-deep-dark border-premium-gold/30 text-white text-base" data-testid="level-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-dark-surface border-premium-gold/30">
                      {LEVELS.map((level) => (
                        <SelectItem key={level} value={level} className="text-white hover:bg-deep-dark capitalize py-3">{level}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <button
                  type="submit"
                  disabled={loading || !formData.sport}
                  className="w-full bg-premium-gold hover:bg-premium-gold-dark text-white font-bold tracking-wider px-6 sm:px-8 py-4 min-h-[48px] rounded-lg transition-all duration-300 hover:shadow-glow-red disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="find-button"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white/20 border-t-white"></div>
                      Finding Your Perfect Match...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Find My Coach
                    </>
                  )}
                </button>
              </form>
            </div>
          </>
        ) : (
          <>
            <div className="mb-8">
              <span className="inline-block px-4 py-1 bg-premium-gold/20 text-premium-gold rounded-full text-sm font-semibold tracking-wider mb-4">
                AI Matched Results
              </span>
              <h1 
                className="text-4xl md:text-5xl font-bold tracking-tight text-white mb-4"
                style={{ fontFamily: 'Fraunces, serif' }}
              >
                Your <span className="gold-gradient">Top Matches</span>
              </h1>
              <p className="text-lg text-white/70">
                AI-matched coaches based on expertise, ratings, and availability
              </p>
            </div>
            
            <div className="space-y-6 mb-8">
              {matches.map((match, index) => (
                <div 
                  key={match.coach_id} 
                  className="bg-dark-surface p-6 rounded-xl border border-premium-gold/30/50 hover:border-premium-gold/30 transition-all"
                  data-testid={`match-${index}`}
                >
                  <div className="flex flex-col md:flex-row items-start gap-6">
                    <div className="relative flex-shrink-0">
                      {match.coach_picture ? (
                        <img src={match.coach_picture} alt={match.coach_name} className="w-24 h-24 rounded-xl border-2 border-premium-gold/50" />
                      ) : (
                        <div className="w-24 h-24 rounded-xl bg-deep-dark border-2 border-premium-gold/30 flex items-center justify-center">
                          <Users className="w-10 h-10 text-white/30" strokeWidth={1.5} />
                        </div>
                      )}
                      <div className="absolute -top-2 -right-2 w-10 h-10 bg-premium-gold rounded-full flex items-center justify-center shadow-glow-red">
                        <span className="text-white font-black text-sm">#{index + 1}</span>
                      </div>
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex flex-col md:flex-row items-start justify-between gap-4 mb-4">
                        <div>
                          <h3 className="text-2xl font-bold text-white" style={{ fontFamily: 'Fraunces, serif' }}>
                            {match.coach_name}
                          </h3>
                          <div className="flex items-center gap-4 mt-2">
                            {match.rating > 0 && (
                              <div className="flex items-center gap-1">
                                <Star className="w-5 h-5 text-premium-gold fill-current" strokeWidth={1.5} />
                                <span className="font-bold text-premium-gold">{match.rating.toFixed(1)}</span>
                              </div>
                            )}
                            <span className="text-white/60">{match.experience_years} years exp</span>
                          </div>
                        </div>
                        <div className="flex flex-col items-start md:items-end gap-2">
                          <div className="inline-flex items-center gap-2 bg-premium-gold/20 text-premium-gold px-4 py-2 rounded-full">
                            <Trophy className="w-4 h-4" strokeWidth={1.5} />
                            <span className="font-bold">{match.match_score}% Match</span>
                          </div>
                          <p className="text-xl font-bold text-premium-gold">₹{match.hourly_rate}/hr</p>
                        </div>
                      </div>
                      
                      <p className="text-white/70 mb-4">{match.coach_bio}</p>
                      
                      <div className="mb-4">
                        <span className="text-sm font-semibold text-white/50 block mb-2">Specializations</span>
                        <div className="flex flex-wrap gap-2">
                          {match.specializations.map((sport) => (
                            <span key={sport} className="text-xs px-3 py-1 bg-deep-dark rounded-full text-white/80 font-medium border border-premium-gold/30/50">
                              {sport}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      <div className="mb-6 p-4 bg-deep-dark/50 rounded-lg border border-premium-gold/30/30">
                        <span className="text-sm font-semibold text-premium-gold block mb-2">Why this match?</span>
                        <ul className="space-y-2">
                          {match.match_reasons.map((reason, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-white/70">
                              <Check className="w-4 h-4 text-premium-gold mt-0.5 flex-shrink-0" />
                              <span>{reason}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <button
                        onClick={() => handleAcceptMatch(match.coach_id)}
                        className="bg-premium-gold hover:bg-premium-gold-dark text-white font-bold tracking-wider px-8 py-3 rounded-lg transition-all duration-300 hover:shadow-glow-red"
                        data-testid={`accept-${match.coach_id}`}
                      >
                        Select This Coach
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="text-center">
              <button
                onClick={handleDefer}
                className="text-white/60 hover:text-white font-semibold transition-colors"
                data-testid="defer-button"
              >
                No thanks, I'll browse all coaches manually
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default FindCoach;
